import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Link,
  Paper,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material';
import LandscapeIcon from '@mui/icons-material/Landscape';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddScopeDialog from '../components/shapeup/AddScopeDialog';
import BetCard from '../components/shapeup/BetCard';
import BetDialog from '../components/shapeup/BetDialog';
import BetGuardrails from '../components/shapeup/BetGuardrails';
import CreateScopeDialog from '../components/shapeup/CreateScopeDialog';
import ScopeCoverageDrawer from '../components/shapeup/ScopeCoverageDrawer';
import CycleWeekIndicator from '../components/shapeup/CycleWeekIndicator';
import HillLegend from '../components/shapeup/HillLegend';
import HillReplay from '../components/shapeup/HillReplay';
import MoveNoteDialog from '../components/shapeup/MoveNoteDialog';
import useScopeActions from '../components/shapeup/useScopeActions';
import { EntityCommentsProvider } from '../components/shapeup/useEntityComments';
import {
  createBetSlackChannel,
  createScopeIssue,
  deleteBet,
  getBetHill,
  getBetHistory,
  getCycleRoster,
  getShapeUpPeople,
  getShapeUpTags,
  syncBetScopes,
  updateBet,
} from '../utils/shapeUpApi';

const CYCLE_STATUS_LABEL = {
  planning: 'Planning',
  active: 'Active',
  cooldown: 'Cool-down',
  complete: 'Complete',
};

const formatRange = (cycle) => {
  if (!cycle?.startDate && !cycle?.endDate) return null;
  const fmt = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  if (cycle.startDate && cycle.endDate) return `${fmt(cycle.startDate)} – ${fmt(cycle.endDate)}`;
  return fmt(cycle.startDate || cycle.endDate);
};

/**
 * One bet on its own page, so the URL can be shared. The cycle overview keeps
 * the Shape Up masthead; here the bet itself is the subject, so the header is
 * just enough context to know which cycle you are looking at.
 */
const BetDetailPage = () => {
  const { betId } = useParams();
  const navigate = useNavigate();
  // Guards the arrival refresh against re-running on every reload of this bet.
  const refreshed = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [createScopeOpen, setCreateScopeOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState(null);
  const [replaying, setReplaying] = useState(false);
  // Bumped after a sync pulls Jira comments in, to make the threads refetch.
  const [commentRefresh, setCommentRefresh] = useState(0);
  const [editing, setEditing] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  // The cycle's roster, which is the only pool this bet's team may draw from, and
  // what each candidate has shipped. Both are conveniences for the edit dialog;
  // neither failing stops the page rendering the bet.
  const [roster, setRoster] = useState([]);
  const [orgCandidates, setOrgCandidates] = useState([]);
  const [tagsByUser, setTagsByUser] = useState(null);
  const [tagSuggestions, setTagSuggestions] = useState([]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const result = await getBetHill(betId);
      setData(result);
      setError(null);

      // History is what makes the trails and the replay possible. A failure here
      // costs those extras and nothing else, so it does not break the page.
      try {
        setHistory(await getBetHistory(betId));
      } catch {
        setHistory(null);
      }
    } catch (err) {
      setError(
        err.response?.status === 404
          ? 'This bet no longer exists. It may have been deleted.'
          : err.response?.data?.message || 'Could not load this bet.'
      );
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [betId]);

  useEffect(() => { load(); }, [load]);

  // Opening a bet is the moment its statuses matter, so pull from Jira once on
  // arrival. Failures stay quiet: the stored scopes still render, and the Sync
  // button reports properly when someone asks for it explicitly.
  useEffect(() => {
    let cancelled = false;

    const refreshFromJira = async () => {
      const epicKey = data?.bet?.jiraEpicKey;
      if (!epicKey || refreshed.current === betId) return;
      refreshed.current = betId;

      setRefreshing(true);
      try {
        await syncBetScopes(betId);
        if (!cancelled) {
          await load({ quiet: true });
          // The arrival sync also pulls comments; let the threads catch up.
          setCommentRefresh(n => n + 1);
        }
      } catch {
        // Deliberately silent; the Sync button surfaces real errors.
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    };

    refreshFromJira();
    return () => { cancelled = true; };
  }, [betId, data?.bet?.jiraEpicKey, load]);

  const cycleId = data?.cycle?.id;
  const organizationId = data?.cycle?.organizationId;

  useEffect(() => {
    if (!cycleId) {
      setRoster([]);
      return;
    }
    getCycleRoster(cycleId)
      .then(result => {
        setRoster(result.members || []);
        // Candidates come back in the same response, so the picker can offer the
        // whole organization without a second request.
        setOrgCandidates(result.candidates || []);
      })
      .catch(() => {
        setRoster([]);
        setOrgCandidates([]);
      });
  }, [cycleId]);

  useEffect(() => {
    if (!organizationId) {
      setTagsByUser(null);
      return;
    }
    getShapeUpPeople(organizationId)
      .then(result => setTagsByUser((result.people || []).reduce((map, person) => {
        map[person.userId] = person;
        return map;
      }, {})))
      .catch(() => setTagsByUser(null));
  }, [organizationId]);

  const loadTagSuggestions = useCallback(() => {
    if (!organizationId) {
      setTagSuggestions([]);
      return;
    }
    getShapeUpTags(organizationId)
      .then(result => setTagSuggestions(result.tags || []))
      .catch(() => setTagSuggestions([]));
  }, [organizationId]);

  useEffect(() => { loadTagSuggestions(); }, [loadTagSuggestions]);

  const applyScopeUpdate = useCallback((scopeId, updates) => {
    setData(prev => prev && ({
      ...prev,
      bet: {
        ...prev.bet,
        scopes: prev.bet.scopes.map(s => (s.id === scopeId ? { ...s, ...updates } : s)),
      },
    }));
  }, []);

  const {
    toast,
    setToast,
    syncingBetId,
    handleMoveScope,
    handleClearOverride,
    handleTogglePark,
    handleExcludeScope,
    handleRenameScope,
    handleHammerScope,
    handleRestate,
    handleChangeStatus,
    handleChangeAssignee,
    handleSync,
    handleAddScope,
    noteRequest,
    saveMoveNote,
    dismissNote,
  } = useScopeActions({
    reload: load,
    applyScopeUpdate,
    onSynced: () => setCommentRefresh(n => n + 1),
  });

  const handleCreateScope = async (payload) => {
    const created = await createScopeIssue(betId, payload);
    await load({ quiet: true });
    setToast({
      severity: 'success',
      message: created.jiraKey ? `Created ${created.jiraKey} in Jira` : `Added ${created.jiraSummary || payload.summary}`,
    });
  };

  const handleCreateSlackChannel = async () => {
    try {
      const result = await createBetSlackChannel(betId);
      await load({ quiet: true });
      setToast({
        severity: result.unresolved?.length || result.failed?.length ? 'warning' : 'success',
        message: result.message,
      });
    } catch (err) {
      setToast({
        severity: 'error',
        message: err.response?.data?.message || 'Could not create the Slack channel.',
      });
    }
  };

  const handleUpdateBet = async (payload) => {
    const updated = await updateBet(betId, payload);
    await load({ quiet: true });
    loadTagSuggestions();
    setToast({ severity: 'success', message: `Saved ${updated.name}` });
  };

  const handleDeleteBet = async (bet) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the bet "${bet.name}" and its scopes? Jira is not affected.`)) return;
    try {
      await deleteBet(bet.id);
      navigate('/shape-up');
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not delete the bet.' });
    }
  };

  const bet = data?.bet;
  const cycle = data?.cycle;
  const jiraEnabled = data?.jiraEnabled !== false;
  const statusOptions = data?.statusPositions ? Object.keys(data.statusPositions) : [];

  // The hill chart wants trails keyed by scope, the API returns them per scope
  // alongside its identity.
  const trailsById = useMemo(() => (history?.scopes || []).reduce((acc, entry) => {
    acc[entry.id] = entry.trail || [];
    return acc;
  }, {}), [history]);

  const hasHistory = (history?.scopes || []).some(entry => (entry.trail || []).length > 1);

  return (
    <EntityCommentsProvider organizationId={cycle?.organizationId} refreshSignal={commentRefresh}>
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/shape-up"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          color="inherit"
          underline="hover"
        >
          <LandscapeIcon fontSize="small" />
          Shape Up
        </Link>
        <Typography color="text.primary" noWrap sx={{ maxWidth: 420 }}>
          {bet?.name || 'Bet'}
        </Typography>
      </Breadcrumbs>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" component={RouterLink} to="/shape-up">Back to Shape Up</Button>}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
      ) : bet ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flexGrow: 1, minWidth: 0 }}>
              {cycle && (
                <>
                  <Chip
                    size="small"
                    label={CYCLE_STATUS_LABEL[cycle.status] || cycle.status}
                    color={cycle.status === 'active' ? 'success' : 'default'}
                    variant={cycle.status === 'active' ? 'filled' : 'outlined'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {cycle.name}
                    {formatRange(cycle) ? ` · ${formatRange(cycle)}` : ''}
                  </Typography>
                  <CycleWeekIndicator cycle={cycle} />
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {hasHistory && (
                <Button
                  size="small"
                  startIcon={<TimelineIcon />}
                  onClick={() => setReplaying(current => !current)}
                >
                  {replaying ? 'Hide replay' : 'Replay'}
                </Button>
              )}
              <HillLegend />
            </Box>
          </Box>

          <BetGuardrails pitch={bet.pitch} pitchUrl={bet.pitchUrl} onSanityCheck={() => setCoverageOpen(true)} />

          {replaying && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                How this bet unfolded
              </Typography>
              <HillReplay
                scopes={bet.scopes.filter(scope => !scope.isHammered)}
                history={history}
                onClose={() => setReplaying(false)}
              />
            </Paper>
          )}

          <BetCard
            bet={bet}
            linkToDetail={false}
            showScopeBoard
            trails={trailsById}
            syncing={syncingBetId === bet.id || refreshing}
            onSync={handleSync}
            onMoveScope={handleMoveScope}
            onClearOverride={handleClearOverride}
            onTogglePark={handleTogglePark}
            onExcludeScope={handleExcludeScope}
            onRenameScope={handleRenameScope}
            onHammerScope={handleHammerScope}
            onRestateScope={handleRestate}
            onChangeStatus={handleChangeStatus}
            onChangeAssignee={handleChangeAssignee}
            onScopeChanged={(scope) => applyScopeUpdate(scope.id, scope)}
            onCommentsRead={(id) => applyScopeUpdate(id, { unreadCommentCount: 0 })}
            definitionOfDone={data?.definitionOfDone || []}
            dodStandard={data?.dodStandard || ''}
            jiraEnabled={jiraEnabled}
            attachmentsEnabled={data?.attachmentsEnabled === true}
            statusOptions={statusOptions}
            onAddScope={() => setScopeDialogOpen(true)}
            onCreateScope={() => setCreateScopeOpen(true)}
            onEditBet={() => setEditing(true)}
            onDeleteBet={handleDeleteBet}
            onCreateSlackChannel={data?.slack?.canCreateChannels ? handleCreateSlackChannel : undefined}
          />
        </>
      ) : !error ? (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Bet not found</Typography>
          <Button variant="contained" component={RouterLink} to="/shape-up">Back to Shape Up</Button>
        </Paper>
      ) : null}

      <BetDialog
        open={editing}
        bet={bet}
        onClose={() => setEditing(false)}
        onSubmit={handleUpdateBet}
        organizationId={organizationId}
        jiraEnabled={jiraEnabled}
        rosterCandidates={roster}
        orgCandidates={orgCandidates}
        tagsByUser={tagsByUser}
        tagSuggestions={tagSuggestions}
        slackAvailable={data?.slack?.canCreateChannels === true}
      />

      <AddScopeDialog
        open={scopeDialogOpen}
        bet={bet}
        onClose={() => setScopeDialogOpen(false)}
        onSubmit={(jiraKey) => handleAddScope(bet.id, jiraKey)}
      />

      <CreateScopeDialog
        open={createScopeOpen}
        bet={bet}
        manual={!jiraEnabled}
        defaultIssueType={data?.defaultIssueType || 'Story'}
        onClose={() => setCreateScopeOpen(false)}
        onSubmit={handleCreateScope}
      />

      <MoveNoteDialog
        open={Boolean(noteRequest)}
        request={noteRequest}
        onSave={saveMoveNote}
        onSkip={dismissNote}
      />

      <ScopeCoverageDrawer
        betId={betId}
        open={coverageOpen}
        onClose={() => setCoverageOpen(false)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
    </EntityCommentsProvider>
  );
};

export default BetDetailPage;
